import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  ClipboardList, Calendar, User as UserIcon, Users, CheckCircle2, Loader2, Circle, Trash2, AlertTriangle, Plus,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "all", label: "All" },
  { v: "todo", label: "To do" },
  { v: "in_progress", label: "Doing" },
  { v: "completed", label: "Done" },
];

const STATUS_ICON = {
  todo: Circle,
  in_progress: Loader2,
  completed: CheckCircle2,
};

function isOverdue(dl, status) {
  if (!dl || status === "completed") return false;
  return dl < new Date().toISOString().slice(0, 10);
}

export default function Tasks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const refresh = async () => {
    try {
      const [t, e] = await Promise.all([apiClient.get("/tasks"), apiClient.get("/employees")]);
      setItems(t.data); setEmployees(e.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await apiClient.put(`/tasks/${id}`, { status });
      setItems((arr) => arr.map((t) => (t.id === id ? { ...t, status } : t)));
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/tasks/${id}`);
      setItems((arr) => arr.filter((t) => t.id !== id));
      toast.success("Task removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const filtered = tab === "all" ? items : items.filter((t) => t.status === tab);

  return (
    <>
      <AppHeader title="Tasks" subtitle={`${items.length} total`} />
      <div className="px-4 pt-4 pb-28 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-xl p-1 flex-1">
            {TABS.map((t) => (
              <button
                key={t.v}
                data-testid={`tasks-tab-${t.v}`}
                onClick={() => setTab(t.v)}
                className={`flex-1 h-9 rounded-lg text-xs font-semibold transition ${tab === t.v ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Link to="/tasks/new" data-testid="tasks-add-btn"
            className="h-11 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 font-semibold text-sm">
            <Plus className="w-4 h-4" /> New
          </Link>
          <Button onClick={() => navigate("/employees")} data-testid="manage-employees-btn"
            variant="outline" className="h-11 rounded-xl border-slate-200 gap-1 px-3">
            <Users className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto grid place-items-center mb-3">
              <ClipboardList className="w-7 h-7 text-slate-400" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">No tasks {tab !== "all" ? `in this list` : "yet"}</p>
            <p className="text-sm text-slate-500">Tap + to assign one</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tasks-list">
            {filtered.map((t) => {
              const Icon = STATUS_ICON[t.status];
              const overdue = isOverdue(t.deadline, t.status);
              return (
                <div key={t.id} data-testid={`task-card-${t.id}`}
                  className={`bg-white rounded-2xl p-4 border ${overdue ? "border-red-200 bg-red-50" : "border-slate-200"}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                      t.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : t.status === "in_progress" ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-500"
                    }`}>
                      <Icon className={`w-4 h-4 ${t.status === "in_progress" ? "animate-spin" : ""}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold text-slate-900 ${t.status === "completed" ? "line-through text-slate-500" : ""}`}>{t.title}</div>
                      {t.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                      <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                        {t.employee_name && <span className="inline-flex items-center gap-1 text-slate-600"><UserIcon className="w-3 h-3" />{t.employee_name}</span>}
                        {t.deadline && (
                          <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-700 font-bold" : "text-slate-600"}`}>
                            {overdue ? <AlertTriangle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />} {t.deadline}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`}
                      className="text-slate-400 hover:text-red-600 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Button size="sm" data-testid={`task-todo-${t.id}`}
                      onClick={() => updateStatus(t.id, "todo")}
                      className={`h-9 rounded-lg text-xs ${t.status === "todo" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>To do</Button>
                    <Button size="sm" data-testid={`task-progress-${t.id}`}
                      onClick={() => updateStatus(t.id, "in_progress")}
                      className={`h-9 rounded-lg text-xs ${t.status === "in_progress" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>In Progress</Button>
                    <Button size="sm" data-testid={`task-done-${t.id}`}
                      onClick={() => updateStatus(t.id, "completed")}
                      className={`h-9 rounded-lg text-xs ${t.status === "completed" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Done</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {employees.length === 0 && !loading && (
          <Link to="/employees" data-testid="hint-add-employee" className="block bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            Tip: Add staff first so you can assign tasks to them →
          </Link>
        )}
      </div>
    </>
  );
}
